import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Trash2, Package, Paperclip, Download, Save, Upload } from 'lucide-react';
import { isoDate } from '../../utils/time';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PageHead, Panel, Register, DetailDrawer, Currency, Field, FormRow, fmtDate, money,
} from './design';

// Items and equipment: things bought for a participant rather than time spent with them.
//
// Assistive technology, home modifications, consumables, bills and vouchers are claimed from
// Services Australia with item details (which item from the official list, whether it was bought
// or loaned, who prescribed it…). Recorded here, each one goes into the month's claim file with
// those details, draws down the right funding, and keeps its evidence (invoice, quote,
// prescription, receipt) ready to upload to the Provider Portal under the name the portal expects.

// The disabled look matters: a paid item's fields are read-only, and must look it.
const INP = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-slate-50 disabled:text-slate-600 disabled:cursor-not-allowed';
const FUNDING = { AT: 'Assistive technology', HM: 'Home modifications', ON: 'Quarterly budget', RC: 'Restorative Care Pathway', EL: 'End-of-Life Pathway', AS: 'AT specified needs' };
// Left blank, the claim file uses the service's usual budget.
const fundingWords = (code) => FUNDING[code] || (code ? 'Other budget' : 'Usual budget for this service');
const EVIDENCE = { INVOICE: 'Invoice', QUOTE: 'Quote', PRESC: 'Prescription', RECEIPT: 'Receipt', OTHER: 'Other' };
const PRODUCT = new Set(['SERV-0061', 'SERV-0062', 'SERV-0063', 'SERV-0064', 'SERV-0065']);
const PROFESSIONAL = new Set(['SERV-0066', 'SERV-0068']);
const ATHM = new Set([...PRODUCT, 'SERV-0066', 'SERV-0067', 'SERV-0068']);
const EMPTY = {
  client_id: '', delivery_date: isoDate(), service_id: '', funding_source: '', item_or_wraparound: 'ITEM',
  item_code: '', item_description: '', wraparound_code: '', wraparound_description: '', prescribed: false,
  purchase_method: 'PURCHASED', loaned_by_org: '', loaned_by_invoice: '', health_prof_code: '', health_prof_other: '',
  first_payment: false, quantity: '1', unit_price: '', third_party: false, supplier: '', notes: '', parent_item_id: '',
};
// Administration (assistive technology) and coordination (home modifications) are charged against an item.
const CAPPED_WRAPS = new Set(['WRAP-004', 'WRAP-012']);
const currentMonth = () => isoDate().slice(0, 7);

// A list that failed to load says so where it would be, with a retry, rather than offering only
// "Choose…" as if there were nothing to choose.
function ListError({ message, onRetry }) {
  return (
    <span className="flex flex-wrap items-center gap-2">
      {message}
      {onRetry && <button type="button" onClick={onRetry} className="btn-ghost btn-sm">Try again</button>}
    </span>
  );
}

export default function AgedCareClaimItems() {
  const [clients, setClients] = useState([]);
  const [period, setPeriod] = useState(currentMonth());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [ref, setRef] = useState({ services: [], items: [], wraparounds: [], professionals: [] });
  // Dropdown lists that failed to load: clients, services (with the wraparounds), items (with the
  // health professionals) and parents. Each holds the message shown under its field.
  const [listErrors, setListErrors] = useState({});
  const [itemsTry, setItemsTry] = useState(0);     // bumped by "Try again" to reload the item list
  const [parentsTry, setParentsTry] = useState(0);
  const [f, setF] = useState(null);           // the item being added or edited
  const [saving, setSaving] = useState(false);
  const [parents, setParents] = useState([]);   // the participant's items, for a wraparound charge
  const [evidence, setEvidence] = useState([]);
  const [evidenceError, setEvidenceError] = useState(null);
  const [upload, setUpload] = useState({ kind: 'INVOICE', description: '' });
  const confirm = useConfirm();
  const listFailed = (key, e, message) => setListErrors((x) => ({ ...x, [key]: e.response?.data?.message || message }));
  const listLoaded = (key) => setListErrors((x) => ({ ...x, [key]: null }));

  const loadClients = useCallback(() => {
    api.get('/aged-care/clients')
      .then((r) => { setClients(r.data || []); setListErrors((x) => ({ ...x, clients: null })); })
      .catch((e) => setListErrors((x) => ({ ...x, clients: e.response?.data?.message || 'Could not load the participants' })));
  }, []);
  const loadServices = useCallback(() => {
    api.get('/aged-care/claim-items/reference')
      .then((r) => { setRef((p) => ({ ...p, services: r.data?.services || [], wraparounds: r.data?.wraparounds || [] })); setListErrors((x) => ({ ...x, services: null })); })
      .catch((e) => setListErrors((x) => ({ ...x, services: e.response?.data?.message || 'Could not load the kinds of item' })));
  }, []);
  useEffect(() => { loadClients(); }, [loadClients]);
  useEffect(() => { loadServices(); }, [loadServices]);

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try { const r = await api.get('/aged-care/claim-items', { params: { period } }); setRows(Array.isArray(r.data) ? r.data : []); }
    catch (e) {
      // Never an empty month on a failed load: it would read as "nothing to claim".
      setRows([]);
      setLoadError(e.response?.data?.message || 'Could not load the items');
    } finally { setLoading(false); }
  }, [period]);
  useEffect(() => { load(); }, [load]);

  // The item lists, health professionals and default funding depend on the service chosen.
  const service = f?.service_id || '';
  useEffect(() => {
    if (!service) { setRef((p) => ({ ...p, items: [], professionals: [] })); listLoaded('items'); return; }
    api.get('/aged-care/claim-items/reference', { params: { service } })
      .then((r) => {
        setRef((p) => ({ ...p, items: r.data?.items || [], professionals: r.data?.professionals || [] }));
        setF((p) => (p && !p.funding_source ? { ...p, funding_source: r.data?.defaultFunding || '' } : p));
        listLoaded('items');
      }).catch((e) => listFailed('items', e, 'Could not load the items for this kind of item'));
  }, [service, itemsTry]);

  const loadEvidence = useCallback(async (id) => {
    setEvidenceError(null);
    if (!id) { setEvidence([]); return; }
    try { const r = await api.get(`/aged-care/claim-items/${id}/evidence`); setEvidence(r.data || []); }
    catch (e) { setEvidence([]); setEvidenceError(e.response?.data?.message || 'Could not load the evidence files'); }
  }, []);

  const openNew = () => { setF({ ...EMPTY }); setEvidence([]); setEvidenceError(null); };
  const openEdit = (it) => {
    const d = (v) => (v == null ? '' : v);
    setF({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, d(it[k])])),
      delivery_date: String(it.delivery_date || '').slice(0, 10), prescribed: !!it.prescribed, first_payment: !!it.first_payment,
      third_party: !!it.third_party, id: it.id, sa_paid_date: it.sa_paid_date, sa_ext_ref: it.sa_ext_ref });
    loadEvidence(it.id);
  };
  const close = useCallback(() => setF(null), []);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const svc = ref.services.find((s) => s.id === f?.service_id);
  const itemCode = ref.items.find((i) => i.c === f?.item_code);
  const wrap = ref.wraparounds.find((w) => w.c === f?.wraparound_code);
  const prof = ref.professionals.find((h) => h.c === f?.health_prof_code);
  const isAthm = ATHM.has(f?.service_id || '');
  const needsParent = isAthm && f?.item_or_wraparound === 'WRAPAROUND' && CAPPED_WRAPS.has(f?.wraparound_code || '');
  const parentClient = needsParent ? f.client_id : '';
  useEffect(() => {
    if (!parentClient) { setParents([]); listLoaded('parents'); return; }
    api.get('/aged-care/claim-items', { params: { client_id: parentClient } })
      .then((r) => { setParents((Array.isArray(r.data) ? r.data : []).filter((x) => x.item_or_wraparound === 'ITEM')); listLoaded('parents'); })
      .catch((e) => { setParents([]); listFailed('parents', e, "Could not load this participant's items"); });
  }, [parentClient, parentsTry]);
  const total = (Number(f?.quantity) || 0) * (Number(f?.unit_price) || 0);
  // Services Australia has paid it: the details are what was claimed, so they are shown, not edited.
  const paid = !!f?.sa_paid_date;

  const save = async () => {
    if (!f.client_id) return toast.error('Choose the participant');
    if (!f.service_id) return toast.error('Choose what kind of item it is');
    if (!(Number(f.unit_price) > 0)) return toast.error('Enter the price');
    setSaving(true);
    try {
      const body = { ...f }; delete body.id; delete body.sa_paid_date; delete body.sa_ext_ref;
      const r = f.id ? await api.put(`/aged-care/claim-items/${f.id}`, body) : await api.post('/aged-care/claim-items', body);
      toast.success(f.id ? 'Item updated' : 'Item recorded. It goes in this month\'s claim file.');
      if (r.data?.warning) toast(r.data.warning, { icon: '⚠️', duration: 7000 });
      if (!f.id) { setF((p) => ({ ...p, id: r.data.id, sa_ext_ref: r.data.sa_ext_ref })); }
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the item'); } finally { setSaving(false); }
  };
  const remove = async (it) => {
    const ok = await confirm('The item comes off the claim, and its funding is given back.', { title: 'Remove this item?', confirmLabel: 'Remove item', danger: true });
    if (!ok) return;
    try { await api.delete(`/aged-care/claim-items/${it.id}`); toast.success('Item removed'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not remove the item'); }
  };

  const addEvidence = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !f?.id) return;
    const fd = new FormData();
    fd.append('file', file); fd.append('kind', upload.kind); fd.append('description', upload.description);
    try {
      await api.post(`/aged-care/claim-items/${f.id}/evidence`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('File attached'); setUpload((p) => ({ ...p, description: '' })); loadEvidence(f.id); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Could not attach the file'); }
  };
  const downloadEvidence = async (ev) => {
    try {
      const r = await api.get(`/aged-care/claim-evidence/${ev.id}/file`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = ev.portal_name; a.click(); window.URL.revokeObjectURL(url);
    } catch (e) {
      // A blob response hides the JSON error, so read it back out.
      let msg = 'Could not download the file';
      try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch { /* keep the default */ }
      toast.error(msg);
    }
  };
  const removeEvidence = async (ev) => {
    const name = ev.description || ev.filename;
    const ok = await confirm(
      `${EVIDENCE[ev.kind] || 'The file'}${name ? ` "${name}"` : ''} comes off this item. Attach it again if the Provider Portal still needs it.`,
      { title: 'Remove this file?', confirmLabel: 'Remove file', danger: true },
    );
    if (!ok) return;
    try { await api.delete(`/aged-care/claim-evidence/${ev.id}`); toast.success('File removed'); loadEvidence(f.id); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not remove the file'); }
  };

  const monthTotal = useMemo(() => rows.reduce((a, r) => a + Number(r.quantity || 0) * Number(r.unit_price || 0), 0), [rows]);

  const columns = [
    { key: 'delivery_date', header: 'Date', width: 110, sortable: true, render: (r) => <span className="tabular-nums">{fmtDate(r.delivery_date)}</span> },
    { key: 'client_name', header: 'Participant', sortable: true, render: (r) => <span className="font-medium text-slate-900">{r.client_name}</span> },
    {
      key: 'service_name', header: 'Item', render: (r) => (
        <span className="min-w-0 block">
          <span className="text-slate-800 block truncate">{r.service_name}{r.item_description ? `: ${r.item_description}` : ''}</span>
          <span className="text-[12px] text-slate-500">{fundingWords(r.funding_source)}{r.item_code ? ` · ${r.item_code}` : ''}{r.wraparound_code ? ` · ${r.wraparound_code}` : ''}</span>
          {r.problem && <span className="block text-[12px] text-amber-700">{r.problem}</span>}
        </span>
      ),
    },
    { key: 'total', header: 'Amount', align: 'right', width: 110, render: (r) => <Currency value={Number(r.quantity || 0) * Number(r.unit_price || 0)} /> },
    {
      key: 'evidence_count', header: 'Evidence', width: 100, render: (r) => (r.evidence_count
        ? <span className="text-slate-700"><Paperclip size={12} className="inline -mt-0.5" /> {r.evidence_count}</span>
        : <span className="text-slate-400">None</span>),
    },
    {
      key: 'status', header: 'Claim', width: 150, render: (r) => (r.sa_paid_date
        ? <span className="text-emerald-800">Paid {fmtDate(r.sa_paid_date)}{r.contribution != null ? `, contribution ${money(r.contribution)}` : ''}</span>
        : <span className="text-slate-500">In this month's claim</span>),
    },
  ];

  return (
    <div>
      <PageHead icon={Package}
        title="Items and equipment"
        purpose="Assistive technology, home modifications, consumables, bills and vouchers bought for a participant. Each goes into the month's claim file with the item details Services Australia asks for, and keeps its evidence ready to upload."
        actions={(
          <>
            <input type="month" aria-label="Month" value={period} onChange={(e) => setPeriod(e.target.value)} className="input-field !w-auto !rounded-lg !py-1.5 !text-[13px]" />
            <button type="button" onClick={openNew} className="btn-primary"><Plus size={15} /> Record an item</button>
          </>
        )}
      />

      <Register
        title="Items this month" count={rows.length} countLabel={rows.length === 1 ? 'item' : 'items'} icon={Package}
        columns={columns} rows={rows} rowKey="id" loading={loading}
        error={loadError} onRetry={load}
        onRowClick={openEdit}
        rowTone={(r) => (r.problem ? 'amber' : undefined)}
        rowActions={(r) => (
          !r.sa_paid_date && (
            <button type="button" onClick={(e) => { e.stopPropagation(); remove(r); }} className="btn-ghost btn-sm text-slate-500" aria-label="Remove item"><Trash2 size={13} /></button>
          )
        )}
        empty={{
          icon: Package,
          title: 'No items this month',
          hint: 'Record equipment, home modifications, consumables or a bill paid for a participant. Visits are claimed from timesheets, not here.',
          action: <button type="button" onClick={openNew} className="btn-primary btn-sm"><Plus size={14} /> Record an item</button>,
        }}
        footer={rows.length ? `Total this month: ${money(monthTotal)}. Items with a note in amber are left out of the claim file until the missing detail is added.` : null}
      />

      {f && (
        <DetailDrawer
          open onClose={close} icon={Package} width="lg"
          title={f.id ? 'Item' : 'Record an item'}
          subtitle={f.sa_ext_ref ? `Claim reference ${f.sa_ext_ref}` : 'It goes into the claim file for the month it was delivered.'}
          footer={(
            <>
              <button type="button" onClick={close} className="btn-secondary">Close</button>
              {!paid && (
                <button type="button" onClick={save} disabled={saving} className="btn-primary"><Save size={15} /> {saving ? 'Saving…' : (f.id ? 'Save item' : 'Record item')}</button>
              )}
            </>
          )}
        >
          <div className="space-y-4">
            {paid && <p className="text-[13px] text-emerald-800">Services Australia paid this on {fmtDate(f.sa_paid_date)}. Its details are shown as claimed and cannot be changed here: a correction is made in the Provider Portal as a claim variation.</p>}
            {/* A disabled fieldset makes every field inside it read-only in one place, so a paid item
                cannot look editable. The evidence below stays outside it: files can still be
                downloaded for the portal. */}
            <fieldset disabled={paid} className="min-w-0 space-y-4">
            <FormRow cols={2}>
              <Field label="Participant" required error={listErrors.clients && <ListError message={listErrors.clients} onRetry={loadClients} />}>
                {(id) => (
                  <select id={id} className={INP} value={f.client_id} onChange={set('client_id')} disabled={!!f.id}>
                    <option value="">Choose…</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                )}
              </Field>
              <Field label="Delivered on" required>
                {(id) => <input id={id} type="date" className={INP} value={f.delivery_date} onChange={set('delivery_date')} />}
              </Field>
            </FormRow>

            <Field label="What kind of item" required hint={svc ? `${svc.type}` : 'From the Support at Home service list.'}
              error={listErrors.services && <ListError message={listErrors.services} onRetry={loadServices} />}>
              {(id) => (
                <select id={id} className={INP} value={f.service_id} onChange={(e) => setF((p) => ({ ...p, service_id: e.target.value, item_code: '', funding_source: '', health_prof_code: '' }))}>
                  <option value="">Choose…</option>
                  {[...new Set(ref.services.map((s) => s.type))].map((type) => (
                    <optgroup key={type} label={type}>
                      {ref.services.filter((s) => s.type === type).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </optgroup>
                  ))}
                </select>
              )}
            </Field>

            {f.service_id && (
              <>
                {isAthm && (
                  <Field label="Is this the item itself, or a service around it?">
                    <div className="flex gap-2">
                      {[['ITEM', 'The item'], ['WRAPAROUND', 'Set up, delivery, training or similar']].map(([v, l]) => (
                        <button key={v} type="button" onClick={() => setF((p) => ({ ...p, item_or_wraparound: v }))} aria-pressed={f.item_or_wraparound === v}
                          className={`px-3 py-2 rounded-lg border text-[13px] ${f.item_or_wraparound === v ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
                      ))}
                    </div>
                  </Field>
                )}

                {(!isAthm || f.item_or_wraparound === 'ITEM') && !PROFESSIONAL.has(f.service_id) && (
                  <Field label="Item" required hint={itemCode ? `Code ${itemCode.c}${itemCode.f ? `, ${itemCode.f}` : ''}` : 'From the official list for this service.'}
                    error={listErrors.items && <ListError message={listErrors.items} onRetry={() => setItemsTry((n) => n + 1)} />}>
                    {(id) => (
                      <select id={id} className={INP} value={f.item_code} onChange={set('item_code')}>
                        <option value="">Choose…</option>
                        {ref.items.map((i) => <option key={i.c} value={i.c}>{i.n}{i.f && i.n === 'Other' ? ` (${i.f})` : ''}</option>)}
                      </select>
                    )}
                  </Field>
                )}
                {itemCode?.o === 1 && (
                  <Field label="Describe the item" required>
                    {(id) => <input id={id} className={INP} value={f.item_description} onChange={set('item_description')} maxLength={500} />}
                  </Field>
                )}
                {isAthm && f.item_or_wraparound === 'WRAPAROUND' && (
                  <FormRow cols={2}>
                    <Field label="Service around the item" required error={listErrors.services && <ListError message={listErrors.services} onRetry={loadServices} />}>
                      {(id) => (
                        <select id={id} className={INP} value={f.wraparound_code} onChange={set('wraparound_code')}>
                          <option value="">Choose…</option>
                          {ref.wraparounds.map((w) => <option key={w.c} value={w.c}>{w.n}</option>)}
                        </select>
                      )}
                    </Field>
                    {needsParent && (
                      <Field label="The item it is for" required hint="Its limit depends on the item's cost."
                        error={listErrors.parents && <ListError message={listErrors.parents} onRetry={() => setParentsTry((n) => n + 1)} />}>
                        {(id) => (
                          <select id={id} className={INP} value={f.parent_item_id || ''} onChange={set('parent_item_id')}>
                            <option value="">Choose…</option>
                            {parents.filter((x) => x.id !== f.id).map((x) => (
                              <option key={x.id} value={x.id}>{fmtDate(String(x.delivery_date || '').slice(0, 10))} · {x.item_description || x.service_name} · {money(Number(x.quantity || 0) * Number(x.unit_price || 0))}</option>
                            ))}
                          </select>
                        )}
                      </Field>
                    )}
                    {wrap?.o === 1 && (
                      <Field label="Describe it" required>
                        {(id) => <input id={id} className={INP} value={f.wraparound_description} onChange={set('wraparound_description')} maxLength={500} />}
                      </Field>
                    )}
                  </FormRow>
                )}
                {PROFESSIONAL.has(f.service_id) && (
                  <FormRow cols={2}>
                    <Field label="Health professional" required error={listErrors.items && <ListError message={listErrors.items} onRetry={() => setItemsTry((n) => n + 1)} />}>
                      {(id) => (
                        <select id={id} className={INP} value={f.health_prof_code} onChange={set('health_prof_code')}>
                          <option value="">Choose…</option>
                          {ref.professionals.map((h) => <option key={h.c} value={h.c}>{h.n}</option>)}
                        </select>
                      )}
                    </Field>
                    {prof?.o === 1 && (
                      <Field label="Describe the professional" required>
                        {(id) => <input id={id} className={INP} value={f.health_prof_other} onChange={set('health_prof_other')} />}
                      </Field>
                    )}
                  </FormRow>
                )}
                {PRODUCT.has(f.service_id) && f.item_or_wraparound === 'ITEM' && (
                  <FormRow cols={3}>
                    <Field label="Bought or loaned">
                      {(id) => (
                        <select id={id} className={INP} value={f.purchase_method} onChange={set('purchase_method')}>
                          <option value="PURCHASED">Bought</option>
                          <option value="LOANED">Loaned</option>
                        </select>
                      )}
                    </Field>
                    {f.purchase_method === 'LOANED' && (
                      <>
                        <Field label="Loaned by" required>{(id) => <input id={id} className={INP} value={f.loaned_by_org} onChange={set('loaned_by_org')} maxLength={160} />}</Field>
                        <Field label="Their invoice number" required>{(id) => <input id={id} className={INP} value={f.loaned_by_invoice} onChange={set('loaned_by_invoice')} maxLength={30} />}</Field>
                      </>
                    )}
                  </FormRow>
                )}

                <FormRow cols={3}>
                  <Field label="Quantity">{(id) => <input id={id} type="number" min="1" step="1" className={INP} value={f.quantity} onChange={set('quantity')} />}</Field>
                  <Field label="Price each ($)" required>{(id) => <input id={id} type="number" min="0" step="0.01" className={INP} value={f.unit_price} onChange={set('unit_price')} />}</Field>
                  <Field label="Claimed from" hint={`Total ${money(total)}`}>
                    {(id) => (
                      // A blank value has its own option: without it the browser shows the first
                      // budget (Assistive technology) as if it had been chosen.
                      <select id={id} className={INP} value={f.funding_source} onChange={set('funding_source')}>
                        <option value="">Choose…</option>
                        {Object.entries(FUNDING).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    )}
                  </Field>
                </FormRow>

                <div className="flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-slate-700">
                  {isAthm && <label className="flex items-center gap-2"><input type="checkbox" checked={f.prescribed} onChange={set('prescribed')} /> Prescribed</label>}
                  {f.service_id === 'SERV-0067' && <label className="flex items-center gap-2"><input type="checkbox" checked={f.first_payment} onChange={set('first_payment')} /> First payment for this home modification</label>}
                  <label className="flex items-center gap-2"><input type="checkbox" checked={f.third_party} onChange={set('third_party')} /> Supplied by a third party</label>
                </div>
                {f.third_party && (
                  <Field label="Supplier">{(id) => <input id={id} className={INP} value={f.supplier} onChange={set('supplier')} maxLength={160} />}</Field>
                )}
                <Field label="Notes" hint="Optional">{(id) => <textarea id={id} rows={2} className={INP} value={f.notes} onChange={set('notes')} />}</Field>
              </>
            )}
            </fieldset>

            {f.id && (
              <Panel title="Evidence" icon={Paperclip} bodyClass="px-4 py-3 space-y-2">
                <p className="text-[12px] text-slate-500">Invoices, quotes, prescriptions and receipts. Downloads are named the way the Provider Portal matches them to this line.</p>
                {evidenceError && (
                  <p role="alert" className="text-[13px] text-red-800"><ListError message={evidenceError} onRetry={() => loadEvidence(f.id)} /></p>
                )}
                {evidence.map((ev) => (
                  <div key={ev.id} className="flex items-center justify-between gap-3 text-[13px]">
                    <span className="min-w-0 truncate">{EVIDENCE[ev.kind] || 'File'}: {ev.description || ev.filename}</span>
                    <span className="flex items-center gap-1 shrink-0">
                      <button type="button" onClick={() => downloadEvidence(ev)} className="btn-ghost btn-sm" title={ev.portal_name}><Download size={13} /> Download for portal</button>
                      <button type="button" onClick={() => removeEvidence(ev)} className="btn-ghost btn-sm text-slate-500" aria-label="Remove file"><Trash2 size={13} /></button>
                    </span>
                  </div>
                ))}
                <div className="flex flex-wrap items-end gap-2 pt-1">
                  <select aria-label="Type of evidence" className={`${INP} !w-auto`} value={upload.kind} onChange={(e) => setUpload((p) => ({ ...p, kind: e.target.value }))}>
                    {Object.entries(EVIDENCE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <input aria-label="Short description" className={`${INP} !w-56`} placeholder="Short description (optional)" value={upload.description} onChange={(e) => setUpload((p) => ({ ...p, description: e.target.value }))} />
                  <label className="btn-secondary btn-sm cursor-pointer"><Upload size={13} /> Attach file<input type="file" accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff" className="hidden" onChange={addEvidence} /></label>
                </div>
              </Panel>
            )}
          </div>
        </DetailDrawer>
      )}
    </div>
  );
}
